import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import {
  Accordion,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Table,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import WidgetIconType from "../WidgetSettingsComponents/WidgetIconType";
import WidgetIconSize from "../WidgetSettingsComponents/WidgetIconSize";
import WidgetIconColor from "../WidgetSettingsComponents/WidgetIconColor";
import WidgetIconPosition from "../WidgetSettingsComponents/WidgetIconPosition";
import WidgetSize from "../WidgetSettingsComponents/WidgetSize";
import WidgetAccessibilityStatementLink from "../WidgetSettingsComponents/AccessibilityStatementLink";
import AnalyticsTrackingCode from "../WidgetSettingsComponents/AnalyticsTracking";
import updateWidgetSettingsService from "../../Services/UpdateWidgetSettingsService";
import WidgetIconSizeMobile from "../WidgetSettingsComponents/WidgetIconSizeMobile";
import voiceSampleText from "../../VoiceSampleText/voiceSampleText.json";

import "../../Assets/scss/pages/widget-settings.scss";
import "../../Assets/scss/components/widget-preview-layout.scss";
import WidgetLayoutMain from "../WidgetPreview";
import useWindowSize from "../../Helpers/useWindowSize";
import { GoogleGetVoicesService } from "../../Services/GoogleTTSService";
import GoogleTTSVoicePropTypes from "../../PropTypes/GoogleTTSVoicePropTypes";
import {
  GoogleSynthesizeSpeechAPI,
  voiceMakerSynthesizeSpeechAPI,
} from "../../Api/GoogleTTSAPI";
import select2LangPropTypes from "../../PropTypes/select2LangPropTypes";
import { useLocation } from "react-router-dom";
import WidgetReportProblemLink from "../WidgetSettingsComponents/ReportProblemLink";
import CustomTrigger from "../WidgetSettingsComponents/CustomTrigger";
import WidgetVisibility from "../WidgetSettingsComponents/WidgetVisibility";
import TourGuide from "../TourGuide";
import VoiceTrigger from "../WidgetSettingsComponents/VoiceTrigger";
import WidgetHideIconType from "../WidgetSettingsComponents/WidgetHideIconType";
import LiveSiteTraslation from "../WidgetSettingsComponents/LiveSiteTranslation";
import ExternalLinkTriggerPopup from "../WidgetSettingsComponents/ExternalLinkTriggerPopup";
import { useTheme } from "../../Context/ThemeContext";
import darkSelectStyles from "../../Helpers/darkSelectStyles";
import WidgetIconTextColor from "../WidgetSettingsComponents/WidgetIconTextColor";
import { SET_MESSAGE } from "../../Actions/Types";
import VoiceCommandPage from "../../Pages/SettingsPage/VoiceCommand";
import { getDomainListWithDetailsService } from "../../Services/DomainService/DomainList";
import FilterContentPage from "../../Pages/SettingsPage/FilterContent";

interface voiceSampleTextPropTypes {
  [key: string]: string;
}

// component to render widget settings
const WidgetSettings = () => {
  // Redux Data
  const {
    currentWebsite,
    accessToken,
    currentWebsitePlanStatus,
    languageList,
    googleVoices,
    user,
    websiteList,
    domainList,
  } = useSelector((store: StoreProptypes) => store);

  // Functions
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const windowWidth = useWindowSize();

  //theme
  const theme = useTheme();

  // State
  const [changeWidgetIconType, setChangeWidgetIconType] = useState<number>(1);
  const [changeHideWidgetIconType, setChangeHideWidgetIconType] =
    useState<number>(34);
  const [changeWidgetIconSize, setChangeWidgetIconSize] = useState<number>(3);
  const [isChangeWidgetIconCustomSize, setIsChangeWidgetIconCustomSize] =
    useState<boolean>(false);
  const [changeWidgetCustomIconSize, setChangeWidgetCustomIconSize] =
    useState<number>(50);
  const [
    isShowCustomWidgetIconSizeMobile,
    setIsShowCustomWidgetIconSizeMobile,
  ] = useState<boolean>(false);
  const [changeWidgetIconSizeMobile, setChangeWidgetIconSizeMobile] =
    useState<number>(3);
  const [
    isChangeWidgetIconCustomSizeMobile,
    setIsChangeWidgetIconCustomSizeMobile,
  ] = useState<boolean>(false);
  const [
    changeWidgetIconCustomSizeMobile,
    setChangeWidgetIconCustomSizeMobile,
  ] = useState<number>(20);
  const [changeWidgetIconColor, setChangeWidgetIconColor] =
    useState<string>("#420083");
  const [changeWidgetIconTextColor, setChangeWidgetIconTextColor] = useState<
    string | null
  >(null);
  const [isWidgetIconCustomTextColor, setIsWidgetIconCustomTextColor] =
    useState<boolean>(false);
  // const [isWidgetIconColor, setIsWidgetIconColor] =
  // useState<boolean>(false);
  const [changeWidgetIconPosition, setChangeWidgetIconPosition] =
    useState<string>("bottom_right");

  const [
    isChangeWidgetIconCustomPosition,
    setIsChangeWidgetIconCustomPosition,
  ] = useState<boolean>(false);
  const [
    changeWidgetIconCustomLeftPosition,
    setChangeWidgetIconCustomLeftPosition,
  ] = useState<number | null>(null);
  const [
    changeWidgetIconCustomRightPosition,
    setChangeWidgetIconCustomRightPosition,
  ] = useState<number | null>(0);
  const [
    changeWidgetIconHorizontalCenterPosition,
    setChangeWidgetIconHorizontalCenterPosition,
  ] = useState<number | null>(0);
  const [
    changeWidgetIconCustomTopPosition,
    setChangeWidgetIconCustomTopPosition,
  ] = useState<number | null>(null);
  const [
    changeWidgetIconCustomBottomPosition,
    setChangeWidgetIconCustomBottomPosition,
  ] = useState<number | null>(0);
  const [
    changeWidgetIconVerticalCenterPosition,
    setChangeWidgetIconVerticalCenterPosition,
  ] = useState<number | null>(0);
  const [changeWidgetSize, setChangeWidgetSize] = useState<number>(0);
  const [changeWidgetMobileVisibility, setChangeWidgetMobileVisibility] =
    useState<number>(1);
  const [
    changeWidgetAccessibilityStatementLink,
    setChangeWidgetAccessibilityStatementLink,
  ] = useState<string>("");
  const [changeWidgetReportProblemLink, setChangeWidgetReportProblemLink] =
    useState<string>("");
  const [isChangeCustomTrigger, setIsChangeCustomTrigger] =
    useState<boolean>(false);
  const [changeCustomTriggerId, setChangeCustomTriggerId] = useState<
    string | null
  >(null);
  const [isDisabledOpenCloseVoice, setIsDisabledOpenCloseVoice] =
    useState<boolean>(false);
  const [isDisabledLiveSiteTranslation, setIsDisabledLiveSiteTranslation] =
    useState<boolean>(false);
  const [
    isDisabledExternalLinkNavigation,
    setIsDisabledExternalLinkNavigation,
  ] = useState<boolean>(false);
  const [DisabledOpenCloseVoiceId, setDisabledOpenCloseVoiceId] = useState<
    string | null
  >(null);
  const [changeGaID, setChangeGaID] = useState<string>("");
  const [changeAdobeID, setChangeAdobeID] = useState<string>("");
  const [selectLangData, setSelectLangData] =
    useState<select2LangPropTypes[]>();
  const [selectedLangData, setSelectedLangData] =
    useState<select2LangPropTypes>();
  const [filteredGoogleVoices, setFilteredGoogleVoices] =
    useState<GoogleTTSVoicePropTypes[]>();
  const [selectedVoiceForPreview, setSelectedVoiceForPreview] =
    useState<GoogleTTSVoicePropTypes>();
  const [selectedVoice, setSelectedVoice] = useState<GoogleTTSVoicePropTypes>();
  const [loadingVoice, setLoadingVoice] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [selectedWebsites, setSelectedWebsites] = useState<number[]>([]);
  const [websiteSearch, setWebsiteSearch] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isPositionValidationDisabled, setIsPositionValidationDisabled] =
    useState(false);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const [localDomainList, setLocalDomainList] = useState<any[]>([]);
  const [isDomainListLoading, setIsDomainListLoading] = useState(false);
  const [totalMainRecord, setTotalMainRecord] = useState<number>(0);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [disabledButton, setDisabledButton] = useState<boolean>(false);
  useEffect(() => {
    // check if user email exists
    if (user) {
      // check if admin user and plan is expired
      if (user?.department === 1 && user?.parent_id === 1) {
        setDisabledButton(true);
      } else {
        setDisabledButton(false);
      }
    }
  }, [user]);
  useEffect(() => {
    // check if language list and default language are available
    if (currentWebsite && currentWebsite.default_language && languageList) {
      // get language object from language list
      const temp = languageList
        .filter((language) => {
          // check if language code is same as default language
          if (language.code === currentWebsite.default_language) {
            return true;
          }
        })
        .map((language) => {
          // create object with required format
          return {
            value: language.code,
            label: language.original_name,
          };
        });

      // set selected language
      if (temp.length) {
        setSelectedLangData(temp[0]);
      }
    }
  }, [currentWebsite, languageList]);

  // Fetch domain list when component mounts
  useEffect(() => {
    if (accessToken) {
      setIsDomainListLoading(true);
      getDomainListWithDetailsService(
        accessToken,
        dispatch,
        t,
        setLocalDomainList,
        100, // limit - get all websites
        0, // offset
        "", // terms
        4, // statusFilter (all)
        "", // startDate
        "", // endDate
        setTotalMainRecord,
        setTotalRecord,
        setIsDomainListLoading,
      );
    }
  }, [accessToken, dispatch, t]);

  useEffect(() => {
    // check if default voice and voice list are available
    if (currentWebsite && currentWebsite.default_voice && googleVoices) {
      // get voice object from the voice list
      const temp = googleVoices.filter((voice) => {
        if (voice.name === currentWebsite.default_voice) {
          return true;
        }
      });

      if (temp.length) {
        setSelectedVoice(temp[0]);
      }
    }
  }, [currentWebsite, googleVoices]);

  useEffect(() => {
    // check is language list is available
    if (languageList) {
      //get language object from the list
      const list = languageList.map((lang) => {
        return {
          value: lang.code,
          label: lang.original_name,
        };
      });
      setSelectLangData(list);
    }
  }, [languageList]);

  useEffect(() => {
    // check if selected lang data is not available, and select language data is available
    if (!selectedLangData && selectLangData) {
      setSelectedLangData(selectLangData[0]);
    }
  }, [selectLangData]);

  useEffect(() => {
    // check if selected voice and google voices list are available
    if (!selectedVoice && filteredGoogleVoices) {
      setSelectedVoice(filteredGoogleVoices[0]);
    }

    // check if language is changed, set first google voice as selected voice
    if (
      currentWebsite &&
      selectedLangData &&
      currentWebsite.default_language !== selectedLangData.value &&
      filteredGoogleVoices
    ) {
      setSelectedVoice(filteredGoogleVoices[0]);
    }
  }, [filteredGoogleVoices, selectedLangData]);

  // function to handle save settings
  const handleSubmitUpdateWidgetSettings = (
    e?: React.MouseEvent<HTMLButtonElement>,
    website_ids?: string,
  ) => {
    e?.preventDefault();

    // check if access token, current website, selected language data and selected voice available
    if (accessToken && currentWebsite && selectedLangData && selectedVoice) {
      // service to update widget settings
      updateWidgetSettingsService(
        accessToken,
        currentWebsite.id,
        dispatch,
        t,
        changeWidgetIconType,
        changeHideWidgetIconType,
        isChangeWidgetIconCustomSize,
        changeWidgetCustomIconSize,
        isShowCustomWidgetIconSizeMobile,
        changeWidgetIconSizeMobile,
        isChangeWidgetIconCustomSizeMobile,
        changeWidgetIconCustomSizeMobile,
        changeWidgetIconSize,
        changeWidgetIconColor,
        changeWidgetIconPosition,
        isChangeWidgetIconCustomPosition,
        changeWidgetIconCustomLeftPosition,
        changeWidgetIconCustomRightPosition,
        changeWidgetIconCustomTopPosition,
        changeWidgetIconCustomBottomPosition,
        changeWidgetIconHorizontalCenterPosition,
        changeWidgetIconVerticalCenterPosition,
        changeWidgetSize,
        changeWidgetAccessibilityStatementLink,
        changeWidgetReportProblemLink,
        isChangeCustomTrigger,
        changeCustomTriggerId,
        changeWidgetMobileVisibility,
        changeGaID,
        changeAdobeID,
        selectedLangData.value,
        selectedVoice.name,
        setShowSuccessModal,
        isDisabledOpenCloseVoice,
        isDisabledLiveSiteTranslation,
        isDisabledExternalLinkNavigation,
        changeWidgetIconTextColor,
        isWidgetIconCustomTextColor,
        website_ids,
      );
    }
  };

  useEffect(() => {
    // check if google voices list is not available
    if (!googleVoices) {
      // service to get google voices list
      GoogleGetVoicesService(dispatch);
    }
  }, [googleVoices]);

  useEffect(() => {
    // check if google voice list and selected language data is available
    if (googleVoices && selectedLangData) {
      console.log("selectedLangData: ", selectedLangData);
      let selectedLangCode = selectedLangData.value;

      // check if lang code is en, then set it to en-US
      if (selectedLangCode === "en") {
        selectedLangCode = "en-US";
      }

      // filter google voices by matching exact language code
      let temp = googleVoices.filter((voice) => {
        const langCode = voice.languageCodes[0];

        // check if language code matches exactly same as google voice language code
        if (selectedLangCode.toLowerCase() === langCode.toLowerCase()) {
          return true;
        }
      });

      // check if filtered voice list is empty
      if (!temp.length) {
        // filter google voices by matching two letters of google voice language code
        temp = googleVoices.filter((voice) => {
          const langCode = voice.languageCodes[0];
          // check if language code matches first two letter of google voice language code
          if (selectedLangCode === langCode.slice(0, 2).toLowerCase()) {
            return true;
          }
        });
      }

      // check if filtered voice list is still empty
      if (!temp.length) {
        // filter google voices by matching two letters of selected language with two letters of google voice language code
        temp = googleVoices.filter((voice) => {
          const langCode = voice.languageCodes[0];
          // check if first two letters of language code matches first two letter of google voice language code
          if (
            selectedLangCode.slice(0, 2) === langCode.slice(0, 2).toLowerCase()
          ) {
            return true;
          }
        });
      }

      // check if filtered voice is still empty
      if (!temp.length) {
        // use default en-us voice for that language
        temp = googleVoices.filter((voice) => {
          const langCode = voice.languageCodes[0];
          // check if lang code is en-US
          if (langCode === "en-US") {
            return true;
          }
        });
      }

      temp = temp.filter((e) => e.name.includes("-"));

      // set filtered google voices list
      temp && setFilteredGoogleVoices(temp);
    }
  }, [googleVoices, selectedLangData]);

  // function to handle play
  const handlePlay = async (voice: GoogleTTSVoicePropTypes) => {
    console.log("voice: ", voice);

    const landCode = voice.languageCodes[0];
    const allText = voiceSampleText as voiceSampleTextPropTypes;
    let selectedText = allText[landCode];

    // check if text for voice is not selected
    if (!selectedText) {
      selectedText = allText[landCode.toLowerCase()];
    }

    // check if text for voice is not selected, set it by slicing language code
    if (!selectedText) {
      selectedText = allText[landCode.slice(0, 2)];
    }

    // check if text for voice is not selected, set it by slicing language code with lowercase
    if (!selectedText) {
      selectedText = allText[landCode.toLowerCase().slice(0, 2)];
    }

    // check if text for voice is not selected, use en-US as fallback
    if (!selectedText) {
      selectedText = allText["en-US"];
    }
    //console.log("selectedText", selectedText);

    // sets selected voice for preview
    setSelectedVoiceForPreview(voice);

    // sets loader on play button
    setLoadingVoice(true);

    if (voice?.isFromOther && voice?.isFromOther === true) {
      try {
        // get voice data from google TTS API
        const data = await voiceMakerSynthesizeSpeechAPI(
          selectedText,
          voice.name,
          voice.languageCodes[0],
        );

        // set audio source
        setAudioSrc(data.path);
        // remove loader
        setLoadingVoice(false);
        // set play
        setIsPlaying(true);
      } catch (err) {
        console.error("Error playing audio:", err);
      }
    } else {
      try {
        // get voice data from google TTS API
        const data = await GoogleSynthesizeSpeechAPI(
          selectedText,
          voice.name,
          voice.languageCodes[0],
        );
        const audioContent = data.audioContent;

        // create audio URL
        const blob = new Blob(
          [
            new Uint8Array(
              atob(audioContent)
                .split("")
                .map((c) => c.charCodeAt(0)),
            ),
          ],
          { type: "audio/mp3" },
        );
        const audioUrl = URL.createObjectURL(blob);

        // set audio source
        setAudioSrc(audioUrl);
        // remove loader
        setLoadingVoice(false);
        // set play
        setIsPlaying(true);
      } catch (err) {
        console.error("Error playing audio:", err);
      }
    }
  };

  // function to stop audio play
  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause(); // Pause the audio
      audioRef.current.currentTime = 0; // Reset playback position
      setIsPlaying(false); // Set isPlaying to false
      setAudioSrc(null); // Reset audio source
    }
  };

  useEffect(() => {
    const shouldDisable =
      currentWebsitePlanStatus.isExpired ||
      (user?.department === 1 && user?.parent_id === 1);
    if (shouldDisable) {
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [currentWebsitePlanStatus, user, currentWebsite]);

  // Combined effect to handle both plan status and position validation
  useEffect(() => {
    const planDisabled =
      currentWebsitePlanStatus.isExpired ||
      (user?.department === 1 && user?.parent_id === 1);
    const finalDisabled = planDisabled || isPositionValidationDisabled;
    setSubmitDisabled(finalDisabled);
  }, [
    currentWebsitePlanStatus,
    user,
    currentWebsite,
    isPositionValidationDisabled,
  ]);

  // Handle website selection modal
  const handleOpenWebsiteModal = () => {
    setShowWebsiteModal(true);
    setWebsiteSearch("");
  };

  const handleSaveWebsiteSettings = () => {
    if (accessToken && currentWebsite && selectedLangData && selectedVoice) {
      const filteredWebsiteIds = selectedWebsites.filter(
        (id) => id !== currentWebsite.id,
      );

      // if (filteredWebsiteIds.length === 0) {
      //   setShowWebsiteModal(false);
      //   return;
      // }

      handleSubmitUpdateWidgetSettings(undefined, filteredWebsiteIds.join(","));
    }
    setShowWebsiteModal(false);
  };

  // Filter domainList to show only active websites (empty canceled_package_only and expired_package_detail)
  const filteredDomainWebsites =
    localDomainList?.filter(
      (website) =>
        (!website.canceled_package_only ||
          website.canceled_package_only.length === 0) &&
        (!website.expired_package_detail ||
          website.expired_package_detail.length === 0),
    ) || [];

  const filteredWebsites = filteredDomainWebsites.filter((website) =>
    website.domain.toLowerCase().includes(websiteSearch.toLowerCase()),
  );
  // Initialize selected websites when modal opens
  useEffect(() => {
    if (showWebsiteModal && localDomainList && currentWebsite) {
      const activeWebsites = localDomainList.filter(
        (website) =>
          (!website.canceled_package_only ||
            website.canceled_package_only.length === 0) &&
          (!website.expired_package_detail ||
            website.expired_package_detail.length === 0),
      );
      setSelectedWebsites(activeWebsites.map((website) => website.id));
    }
  }, [showWebsiteModal, localDomainList, currentWebsite]);
  const handleWebsiteToggle = (websiteId: number) => {
    if (websiteId === currentWebsite?.id) return;
    setSelectedWebsites((prev) =>
      prev.includes(websiteId)
        ? prev.filter((id) => id !== websiteId)
        : [...prev, websiteId],
    );
  };

  const handleSelectAllWebsites = (checked: boolean) => {
    if (checked) {
      setSelectedWebsites(filteredWebsites.map((website) => website.id));
    } else {
      setSelectedWebsites(currentWebsite ? [currentWebsite.id] : []);
    }
  };

  // Update indeterminate state for select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = false; // 🔥 always false
    }
  }, [selectedWebsites, filteredWebsites]);

  console.log(
    "currentWebsitePlanStatus",
    currentWebsitePlanStatus.isFreeWidget,
  );
  return (
    <>
      <TourGuide />
      <div className="aioa_dashboard-widget-settings aioa_dashboard-settings-with-preview-wrapper">
        <Row>
          <Col className="aioa-dashboard-widget-preview-settings-col aioa_dashboard-widget-settings-wrapper">
            <Tabs
              defaultActiveKey="icon"
              id="widget-settings-tabs"
              className="mb-3 widget-settings-tabs"
              mountOnEnter
              unmountOnExit
            >
              {/* --- BASIC ICON & APPEARANCE --- */}
              <Tab
                eventKey="icon"
                title={t("Icon Design")}
                data-tour="widget-icon"
              >
                <div className="widget-icon-settings">
                  <div>
                    <WidgetIconType
                      setChangeWidgetIconType={setChangeWidgetIconType}
                      changeWidgetIconColor={changeWidgetIconColor}
                    />
                  </div>{" "}
                  {/* <hr /> */}
                  <div>
                    <WidgetIconSize
                      setChangeWidgetIconSize={setChangeWidgetIconSize}
                      changeWidgetIconColor={changeWidgetIconColor}
                      setIsChangeWidgetIconCustomSize={
                        setIsChangeWidgetIconCustomSize
                      }
                      setChangeWidgetCustomIconSize={
                        setChangeWidgetCustomIconSize
                      }
                      isShowCustomWidgetIconSizeMobile={
                        isShowCustomWidgetIconSizeMobile
                      }
                      setIsShowCustomWidgetIconSizeMobile={
                        setIsShowCustomWidgetIconSizeMobile
                      }
                    />
                  </div>
                  <hr />
                  {!currentWebsitePlanStatus.isFreeWidget &&
                    isShowCustomWidgetIconSizeMobile && (
                      <>
                        <div>
                          <WidgetIconSizeMobile
                            setChangeWidgetIconSizeMobile={
                              setChangeWidgetIconSizeMobile
                            }
                            setIsChangeWidgetIconCustomSizeMobile={
                              setIsChangeWidgetIconCustomSizeMobile
                            }
                            setChangeWidgetIconCustomSizeMobile={
                              setChangeWidgetIconCustomSizeMobile
                            }
                          />
                        </div>
                        <hr />
                      </>
                    )}
                  <div className="widget-color-change">
                    <WidgetIconColor
                      setChangeWidgetIconColor={setChangeWidgetIconColor}
                      changeWidgetIconColor={changeWidgetIconColor}
                      // setIsWidgetIconColor={setIsWidgetIconColor}
                      // isWidgetIconColor={isWidgetIconColor}
                    />

                    {!currentWebsitePlanStatus.isFreeWidget && (
                      <>
                        <div>
                          <WidgetIconTextColor
                            setChangeWidgetIconTextColor={
                              setChangeWidgetIconTextColor
                            }
                            changeWidgetIconTextColor={
                              changeWidgetIconTextColor
                            }
                            // setIsWidgetIconCustomTextColor={
                            //   setIsWidgetIconCustomTextColor
                            // }
                            // isWidgetIconCustomTextColor={
                            //   isWidgetIconCustomTextColor
                            // }
                          />
                        </div>
                        <hr />
                      </>
                    )}
                  </div>
                  <hr />
                  <div>
                    <WidgetIconPosition
                      setChangeWidgetIconPosition={setChangeWidgetIconPosition}
                      setIsChangeWidgetIconCustomPosition={
                        setIsChangeWidgetIconCustomPosition
                      }
                      setChangeWidgetIconCustomLeftPosition={
                        setChangeWidgetIconCustomLeftPosition
                      }
                      setChangeWidgetIconCustomRightPosition={
                        setChangeWidgetIconCustomRightPosition
                      }
                      setChangeWidgetIconCustomTopPosition={
                        setChangeWidgetIconCustomTopPosition
                      }
                      setChangeWidgetIconCustomBottomPosition={
                        setChangeWidgetIconCustomBottomPosition
                      }
                      setChangeWidgetIconHorizontalCenterPosition={
                        setChangeWidgetIconHorizontalCenterPosition
                      }
                      setChangeWidgetIconVerticalCenterPosition={
                        setChangeWidgetIconVerticalCenterPosition
                      }
                      setSubmitDisabled={setIsPositionValidationDisabled}
                    />
                  </div>
                  <hr />
                  <div>
                    <WidgetSize setChangeWidgetSize={setChangeWidgetSize} />
                  </div>
                  <hr />
                  {!currentWebsitePlanStatus.isFreeWidget && (
                    <>
                      <div>
                        <WidgetHideIconType
                          setChangeHideWidgetIconType={
                            setChangeHideWidgetIconType
                          }
                          changeHideWidgetIconType={changeHideWidgetIconType}
                        />
                      </div>
                    </>
                  )}
                </div>
              </Tab>

              <Tab
                eventKey="Communication Settings"
                title={t("Communication Settings")}
                data-tour="Communication Settings"
              >
                <div className="widget-icon-settings compact-settings">
                  {/* Live Site Translation - Compact */}
                  {currentWebsite?.live_site_translation_enabled && (
                    <>
                      <LiveSiteTraslation
                        setIsDisabledLiveSiteTranslation={
                          setIsDisabledLiveSiteTranslation
                        }
                      />
                      {/* <hr className="compact-hr" /> */}
                    </>
                  )}

                  {/* Language Selector - Compact with smaller box */}
                  {!currentWebsitePlanStatus.isFreeWidget && (
                    <>
                      <div className="compact-field">
                        <Form.Label
                          htmlFor="language_select"
                          className="compact-label"
                          style={{ fontSize: 15 }}
                        >
                          {t("Widget language")}
                        </Form.Label>
                        <Select
                          id="language_select"
                          onChange={(e) => {
                            setSelectedLangData({
                              label: e ? e.label : "",
                              value: e ? e.value : "",
                            });
                          }}
                          options={selectLangData}
                          defaultValue={selectedLangData}
                          value={selectedLangData}
                          closeMenuOnSelect={true}
                          backspaceRemovesValue={true}
                          isClearable={false}
                          isSearchable={true}
                          placeholder={`${t("Select default language")}...`}
                          className="aioa_dashboard-custom-language-select compact-select"
                          styles={{
                            ...(theme.theme === "dark" ? darkSelectStyles : {}),
                            control: (base) => ({
                              ...base,
                              minHeight: "30px",
                              height: "30px",
                              fontSize: "12px",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "30px",
                              lineHeight: "30px",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                              height: "28px",
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: "28px",
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                              height: "28px",
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: "4px",
                              height: "28px",
                            }),
                            menu: (base) => ({
                              ...base,
                              fontSize: "12px",
                            }),
                            option: (base) => ({
                              ...base,
                              fontSize: "12px",
                              padding: "6px 10px",
                            }),
                          }}
                          classNamePrefix="lang-select"
                          theme={(theme) => ({
                            ...theme,
                            borderRadius: 4,
                            colors: {
                              ...theme.colors,
                              primary25: "#d9cce6",
                              primary: "#420083",
                            },
                          })}
                        />
                      </div>
                             {/* Voice Selection - Compact Table */}
                      {filteredGoogleVoices && (
                        <>
                          {audioSrc && (
                            <audio
                              ref={audioRef}
                              src={audioSrc}
                              onEnded={() => setIsPlaying(false)}
                              autoPlay
                            />
                          )}

                          <div className="compact-voice-table">
                            <table className="compact-table">
                              <thead>
                                <tr>
                                  <th>{t("Language")}</th>
                                  <th>{t("Name")}</th>
                                  <th>{t("Gender")}</th>
                                  <th className="text-center">
                                    {t("Preview")}
                                  </th>
                                  <th className="text-center">
                                    <span className="visually-hidden">
                                      {t("Select")}
                                    </span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredGoogleVoices.map((voice) => (
                                  <tr key={voice.name}>
                                    <td className="compact-cell">
                                      {voice.languageCodes[0]}
                                    </td>
                                    <td className="compact-cell">
                                      {voice.name}
                                    </td>
                                    <td className="compact-cell">
                                      {voice.ssmlGender}
                                    </td>
                                    <td className="text-center compact-cell-action">
                                      <Button
                                        variant="link"
                                        aria-label={
                                          selectedVoiceForPreview?.name ===
                                            voice.name && isPlaying
                                            ? t("Stop VoiceName", {
                                                voiceName: voice.name,
                                              })
                                            : t("Play VoiceName", {
                                                voiceName: voice.name,
                                              })
                                        }
                                        onClick={() =>
                                          selectedVoiceForPreview?.name ===
                                            voice.name && isPlaying
                                            ? handleStop()
                                            : handlePlay(voice)
                                        }
                                        disabled={loadingVoice}
                                        className="compact-button"
                                      >
                                        <span className="material-symbols-outlined compact-icon">
                                          {selectedVoiceForPreview?.name ===
                                            voice.name && isPlaying
                                            ? "stop_circle"
                                            : "play_circle"}
                                        </span>
                                      </Button>
                                    </td>
                                    <td className="text-center compact-cell-action">
                                      <Form.Check
                                        type="radio"
                                        id={voice.name}
                                        aria-label={voice.name}
                                        name="voice-selection"
                                        onChange={() => setSelectedVoice(voice)}
                                        checked={selectedVoice === voice}
                                        className="compact-radio"
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                      <hr />
                      {/* Voice Command - Available for all non-free plans */}
                      {!currentWebsitePlanStatus.isFreeWidget && (
                        <VoiceCommandPage disabledButton={submitDisabled} />
                      )}

                   
                    </>
                  )}
                </div>
              </Tab>

              {/* Analytics - Available for all non-free plans */}
              {!currentWebsitePlanStatus.isFreeWidget && (
                <Tab eventKey="analytics" title={t("Analytics")}>
                  <div className="widget-icon-settings">
                    <AnalyticsTrackingCode
                      setChangeAdobeID={setChangeAdobeID}
                      setChangeGaID={setChangeGaID}
                    />
                  </div>
                </Tab>
              )}

              {/* Analytics for Free Plan - Only show this if NOT free widget */}
              {/* Note: Remove the duplicate analytics tab from outside this condition */}
              {currentWebsitePlanStatus.isFreeWidget && (
                <Tab eventKey="analytics-free" title={t("Analytics")}>
                  <AnalyticsTrackingCode
                    setChangeAdobeID={setChangeAdobeID}
                    setChangeGaID={setChangeGaID}
                  />
                </Tab>
              )}
              <Tab
                eventKey="accessibility-link"
                title={t("Advanced Settings")}
                data-tour="widget-accessibility-statement"
              >
                <div className="widget-icon-settings">
                  <div className="row">
                    <div className="col">
                      {!currentWebsitePlanStatus.isFreeWidget && (
                        <WidgetAccessibilityStatementLink
                          setChangeWidgetAccessibilityStatementLink={
                            setChangeWidgetAccessibilityStatementLink
                          }
                        />
                      )}
                    </div>
                    <div className="col">
                      {/* Report Problem - Available for all non-free plans */}
                      {!currentWebsitePlanStatus.isFreeWidget && (
                        <WidgetReportProblemLink
                          setChangeWidgetReportProblemLink={
                            setChangeWidgetReportProblemLink
                          }
                        />
                      )}
                    </div>
                  </div>
                  <hr className="compact-hr" />
                  <div className="row">
                    <div className="col">
                      {/* External Link Popup - Available for all non-free plans */}
                      {!currentWebsitePlanStatus.isFreeWidget && (
                        <ExternalLinkTriggerPopup
                          setIsExternalLinkTrigger={
                            setIsDisabledExternalLinkNavigation
                          }
                        />
                      )}
                    </div>
                    <div className="col">
                      {/* Open/Close Sound - Available for all non-free plans */}
                      {!currentWebsitePlanStatus.isFreeWidget && (
                        <VoiceTrigger
                          setIsOpenCloseWidgetTrigger={
                            setIsDisabledOpenCloseVoice
                          }
                        />
                      )}
                    </div>
                  </div>
                  <hr className="compact-hr" />

                  {!currentWebsitePlanStatus.isFreeWidget && (
                    <CustomTrigger
                      setChangeCustomTriggerId={setChangeCustomTriggerId}
                      setIsChangeCustomTrigger={setIsChangeCustomTrigger}
                    />
                  )}
                   <hr/>
                  {!currentWebsitePlanStatus.isFreeWidget && (
                    <div className="mt-1">
                      <FilterContentPage disabledButton={disabledButton} />
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>

            {/* Action Buttons */}
            {(!currentWebsitePlanStatus.isExpired ||
              currentWebsitePlanStatus.planExpiredData !== null ||
              currentWebsitePlanStatus.planName !== null) && (
              <div className="aioa_dashboard-widget-settings-screen-btns mt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  onClick={(e) => handleSubmitUpdateWidgetSettings(e)}
                  disabled={submitDisabled}
                >
                  {t("Save Settings")}
                </Button>
                {filteredWebsites?.length > 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleOpenWebsiteModal}
                    disabled={submitDisabled}
                  >
                    {t("Apply to All")}
                  </Button>
                )}
              </div>
            )}
          </Col>
          {/* Preview Column - visible on large screens */}
          {windowWidth.windowSize > 1199.98 &&
            location.pathname !== "/front/web-widget-settings" && (
              <Col className="aioa-dashboard-widget-preview-col tour-widget-setting-preview">
                <WidgetLayoutMain
                  changeWidgetIconType={changeWidgetIconType}
                  changeHideWidgetIconType={changeHideWidgetIconType}
                  changeWidgetIconSize={changeWidgetIconSize}
                  isChangeWidgetIconCustomSize={isChangeWidgetIconCustomSize}
                  changeWidgetCustomIconSize={changeWidgetCustomIconSize}
                  changeWidgetIconColor={changeWidgetIconColor}
                  changeWidgetIconPosition={changeWidgetIconPosition}
                  isChangeWidgetIconCustomPosition={
                    isChangeWidgetIconCustomPosition
                  }
                  changeWidgetIconCustomLeftPosition={
                    changeWidgetIconCustomLeftPosition
                  }
                  changeWidgetIconCustomRightPosition={
                    changeWidgetIconCustomRightPosition
                  }
                  changeWidgetIconCustomTopPosition={
                    changeWidgetIconCustomTopPosition
                  }
                  changeWidgetIconCustomBottomPosition={
                    changeWidgetIconCustomBottomPosition
                  }
                  changeWidgetIconHorizontalCenterPosition={
                    changeWidgetIconHorizontalCenterPosition
                  }
                  changeWidgetIconVerticalCenterPosition={
                    changeWidgetIconVerticalCenterPosition
                  }
                  changeWidgetSize={changeWidgetSize}
                  isShowIconPreview={true}
                  selectedLangData={selectedLangData}
                  changeWidgetIconTextColor={changeWidgetIconTextColor}
                  isWidgetIconCustomTextColor={isWidgetIconCustomTextColor}
                />
              </Col>
            )}
        </Row>
      </div>

      {/* Success Modal */}
      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Widget Settings Updated!")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("widget settings update success msg")}</p>
        </Modal.Body>
      </Modal>

      {/* Website Selection Modal */}
      <Modal
        show={showWebsiteModal}
        onHide={() => setShowWebsiteModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Select Websites")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form.Label className="mb-0">
              {t("Select websites to apply settings")}
            </Form.Label>
            <Form.Check
              ref={selectAllCheckboxRef}
              type="checkbox"
              label={t("Select All")}
              checked={
                filteredWebsites.length > 0 &&
                filteredWebsites.every((site) =>
                  selectedWebsites.includes(site.id),
                )
              }
              onChange={(e) => handleSelectAllWebsites(e.target.checked)}
            />
          </div>

          <Form.Control
            type="text"
            placeholder={t("Search Website...")}
            value={websiteSearch}
            onChange={(e) => setWebsiteSearch(e.target.value)}
            className="mb-3"
          />

          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {filteredWebsites.length === 0 ? (
              <div className="text-center py-3">{t("No Website found")}</div>
            ) : (
              filteredWebsites.map((website) => {
                const isPrimaryDomain = website.id === currentWebsite?.id;
                return (
                  <div
                    key={website.id}
                    className="d-flex align-items-center mb-2 p-2 border rounded"
                  >
                    <Form.Check
                      type="checkbox"
                      id={`website-${website.id}`}
                      checked={
                        website.id === currentWebsite?.id
                          ? true
                          : selectedWebsites.includes(website.id)
                      }
                      onChange={() => handleWebsiteToggle(website.id)}
                      disabled={website.id === currentWebsite?.id}
                      className="me-3"
                    />
                    <div>
                      <div className="fw-bold">
                        {website.domain}
                        {isPrimaryDomain && (
                          <span className="badge bg-primary ms-2">
                            {t("Primary")}
                          </span>
                        )}
                      </div>
                      {website.title && (
                        <small className="text-muted">{website.title}</small>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowWebsiteModal(false)}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveWebsiteSettings}
            disabled={selectedWebsites.length === 0}
          >
            {t("save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WidgetSettings;
